import { getWeekNumber, toJSONDate } from '@/core/utils'
import {
  TimeReport,
  TransportReport,
  Report,
  ReportType,
  ReportWeekSummary,
  DietReport,
  SicknessReport
} from '@/models/report.model'
import reportRepository from '@/repositories/reportRepository'
import { ActionContext, Module } from 'vuex'
import { useToast } from '../toast.store'

type UpdateReportPayload = {
  key: keyof Report
  value: any
}

type ReportWeekPayload = {
  weekNumber: number
  year: number
}

interface ReportState {
  currentType: ReportType | undefined
  currentReport:
    | TimeReport
    | TransportReport
    | DietReport
    | SicknessReport
    | undefined
  reportWeek: ReportWeekSummary[] | undefined
  reportDateSelected: Date | undefined
  reportWeekSelected: ReportWeekPayload | undefined
}

type ReportActionContext = ActionContext<ReportState, unknown>

const componentMap = {
  DateSelector: 'DateSelector',
  TimeIntervalSelector: 'TimeIntervalSelector',
  TransportTypeSelector: 'TransportTypeSelector',
  DistanceKmInput: 'DistanceKmInput',
  RemarkSection: 'RemarkSection',
  SaveReportButton: 'SaveReportButton',
  WeeklyActivity: 'WeeklyActivity',
  DietSelection: 'DietSelection',
  DietOverview: 'DietOverview'
}

const reportModule = {
  namespaced: true,
  state: (): ReportState => ({
    currentReport: undefined,
    currentType: undefined,
    reportWeek: [],
    reportDateSelected: undefined,
    reportWeekSelected: {
      weekNumber: getWeekNumber(new Date()),
      year: new Date().getFullYear()
    }
  }),
  getters: {
    reportWeekComponents: (state: ReportState): string[] => {
      switch (state.currentType) {
        case ReportType.TRANSPORT:
          return [componentMap.WeeklyActivity]
        case ReportType.TIME:
          return [componentMap.WeeklyActivity]
        case ReportType.SICK:
          return [componentMap.WeeklyActivity]
        default:
          return []
      }
    },
    reportCreateComponents: (state: ReportState): string[] => {
      switch (state.currentType) {
        case ReportType.TRANSPORT:
          return [
            componentMap.DateSelector,
            componentMap.TransportTypeSelector,
            componentMap.TimeIntervalSelector,
            componentMap.DistanceKmInput,
            componentMap.RemarkSection,
            componentMap.SaveReportButton
          ]
        case ReportType.TIME:
          return [
            componentMap.DateSelector,
            componentMap.TimeIntervalSelector,
            componentMap.RemarkSection,
            componentMap.SaveReportButton
          ]
        case ReportType.SICK:
          return [
            componentMap.DateSelector,
            componentMap.TimeIntervalSelector,
            componentMap.RemarkSection,
            componentMap.SaveReportButton
          ]
        case ReportType.DIET:
          return [componentMap.DietSelection, componentMap.SaveReportButton]
        default:
          return []
      }
    },
    reportWeek: (state: ReportState): ReportWeekSummary[] | undefined => state.reportWeek,
    reportType: (state: ReportState): ReportType | undefined => state.currentType,
    reportSelectedDate: (state: ReportState): Date | string | undefined => state.reportDateSelected,
    currentReport: (state: ReportState) => state.currentReport
  },
  mutations: {
    INITIALIZE_CURRENT_REPORT (state: ReportState, type: ReportType) {
      state.currentType = type
      switch (type) {
        case ReportType.TIME:
          state.currentReport = new TimeReport()
          break
        case ReportType.TRANSPORT:
          state.currentReport = new TransportReport()
          break
        case ReportType.DIET:
          state.currentReport = new DietReport()
          break
        case ReportType.SICK:
          state.currentReport = new SicknessReport()
          break
        default:
          state.currentReport = undefined
      }
    },
    UPDATE_REPORT (state: ReportState, payload: UpdateReportPayload) {
      const { key, value } = payload
      let adjustedValue = value

      if (typeof value === 'string' && value.trim() === '') {
        adjustedValue = undefined
      }
      if (state.currentReport) {
        (state.currentReport as any)[key] = adjustedValue
        console.log(`UPDATE_REPORT UPDATED key ${key} WITH VALUE ${value}`)
      } else {
        console.log('no current report')
      }
    },
    SET_REPORT_WEEK (state: ReportState, reportWeek: ReportWeekSummary[]) {
      state.reportWeek = reportWeek
    },
    async SET_REPORT_SELECTED_DATE (state: ReportState, date: Date) {
      state.reportDateSelected = date
      let response
      switch (state.currentType) {
        case ReportType.TRANSPORT:
          response = await reportRepository.getTransportReportDetails(date)
          break
        case ReportType.SICK:
          response = await reportRepository.getSicknessReportDetails(date)
          break
        case ReportType.TIME:
          response = await reportRepository.getTimeReportDetails(date)
          break
      }
      if (!!response && response.data && response.data.id) {
        state.currentReport = response.data
      } else if (!!response && !response.data.id) {
        switch (state.currentType) {
          case ReportType.TIME:
            state.currentReport = new TimeReport()
            state.currentReport.date = date
            break
          case ReportType.TRANSPORT:
            state.currentReport = new TransportReport()
            state.currentReport.date = date
            break
          case ReportType.DIET:
            state.currentReport = new DietReport()
            break
          case ReportType.SICK:
            state.currentReport = new SicknessReport()
            state.currentReport.date = date
            break
          default:
            state.currentReport = undefined
        }
        state.reportDateSelected = date
      }
    },
    SET_REPORT_SELECTED_WEEK (state: ReportState, payload: ReportWeekPayload) {
      state.reportWeekSelected = payload
    },
    SET_DIET_DETAILS (state: ReportState, dietReport: DietReport) {
      if (state.currentReport) {
        state.currentReport = dietReport
      }
    }
  },
  actions: {
    setReportType ({ commit, getters }: ReportActionContext, type: ReportType) {
      commit('INITIALIZE_CURRENT_REPORT', type)
    },
    getCurrentReport ({ state }: ReportActionContext) {
      return state.currentReport
    },
    async saveReport ({ state, commit }: ReportActionContext) {
      try {
        let response
        const currentReport = state.currentReport
        switch (state.currentType) {
          case ReportType.TIME:
            response = await reportRepository.saveTimeReport(
              currentReport as TimeReport
            )
            break
          case ReportType.SICK:
            response = await reportRepository.saveSicknessReport(
              currentReport as SicknessReport
            )
            break
          case ReportType.TRANSPORT:
            response = await reportRepository.saveTransportReport(
              currentReport as TransportReport
            )
            break
          case ReportType.DIET: {
            const casted = currentReport as DietReport
            if (casted.amountConnectingHours === undefined || casted.daysAmountBoarding === undefined || !casted.amountConnectingHours === undefined) {
              return
            }
            response = await reportRepository.saveDietReport(
              currentReport as DietReport
            )
            break
          }
          default:
            console.log('Unsupported type')
            break
        }
        if (response && (response.status === 200 || response.status === 204)) {
          return { success: true, type: state.currentType }
        } else {
          return { success: false, result: response }
        }
      } catch (error: any) {
        if (error.response.status === 403 && error.response.data.error === 'INVALID_REPORT_TIME') {
          const { addToast } = useToast();
          addToast({
            type: 'error',
            title: 'Ugyldigt tidspunkt for indberetning',  // Custom title for the specific error
            description: error.response.data.message,     // Custom message from the server
            duration: 8000  // This is the duration (ms) the toast will show; adjust as necessary
          });
        }
        return { success: false, result: null }
      }
    },
    async getCurrentReportWeekSummary ({ state, commit }: ReportActionContext, payload: ReportWeekPayload) {
      let result
      switch (state.currentType) {
        case ReportType.TIME:
          result = await reportRepository.getTimeReportWeekOverview(
            payload.weekNumber,
            payload.year
          )
          break
        case ReportType.SICK:
          result = await reportRepository.getSicknessReportWeekOverview(
            payload.weekNumber,
            payload.year
          )
          break
        case ReportType.TRANSPORT:
          result = await reportRepository.getTransportReportWeekOverview(
            payload.weekNumber,
            payload.year
          )
          break
        default:
          result = undefined
          break
      }
      if (result) {
        console.log('setting report week', result.data)
        commit('SET_REPORT_WEEK', result.data)
      }
    },
    async fetchDietReportDetails ({ commit, state }: ReportActionContext, { weekNumber, year }: ReportWeekPayload) {
      if (state.currentReport) {
        try {
          const response = await reportRepository.getDietReportDetails(
            weekNumber,
            year
          )
          if (response.data.id) {
            commit('SET_DIET_DETAILS', response.data)
          }
        } catch (error) {
          console.error('Failed to fetch diet report details:', error)
        }
      }
    }
  }
}

export default reportModule
